import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { TestAutomationTools } from '@shared/constants/test-automation';
import { Odt2Platform } from '@shared/constants/odt';
import { RestService } from './rest.service';
import { AppConfigService } from './app-config.service';
import { SharedCommonUtility } from '../../../shared/utils/common.utility';
import { OdtService } from './odt.service';

/** Bridges the Tools & Extensions tool ids to the platform vocabulary the ODT v2 endpoint accepts. */
const ODT2_TOOL_TO_PLATFORM: Partial<Record<TestAutomationTools, Odt2Platform>> = {
  [TestAutomationTools.odt2_macos]: Odt2Platform.macos,
  [TestAutomationTools.odt2_win_x64]: Odt2Platform.winX64,
  [TestAutomationTools.odt2_win_arm64]: Odt2Platform.winArm64,
  [TestAutomationTools.odt2_deb_x64]: Odt2Platform.debX64,
  [TestAutomationTools.odt2_deb_arm64]: Odt2Platform.debArm64,
  [TestAutomationTools.odt2_rpm_x64]: Odt2Platform.rpmX64,
  [TestAutomationTools.odt2_rpm_arm64]: Odt2Platform.rpmArm64,
};

@Injectable({ providedIn: 'root' })
export class TestAutomationService {
  constructor(
    private restService: RestService,
    private configService: AppConfigService,
    private httpClient: HttpClient,
    private odtService: OdtService,
  ) {}

  public download(toolId: TestAutomationTools): Observable<void> {
    return this.getToolUrl(toolId).pipe(
      switchMap((toolUrl: string): Observable<void> => {
        return this.restService.download(toolUrl);
      }),
    );
  }

  public getToolUrl(toolId: TestAutomationTools): Observable<string> {
    const downloadToolsUrl: string = this.configService.getDownloadToolsUrl();
    const downloadToolsIndexPath: string = this.configService.getDownloadToolsIndexPath();

    if (toolId === TestAutomationTools.odt_mac) {
      return of(this.odtService.getDownloadUrl());
    }

    const odt2Platform: Odt2Platform | undefined = ODT2_TOOL_TO_PLATFORM[toolId];
    if (SharedCommonUtility.notNullish(odt2Platform)) {
      return of(this.odtService.getV2DownloadUrl(odt2Platform));
    }

    if (toolId === TestAutomationTools.dca_windows) {
      const downloadToolsDcaPath: string = this.configService.getDownloadToolsDcaPathWindows();
      if (SharedCommonUtility.isNullish(downloadToolsDcaPath)) {
        throw new Error('Unknown tool path for tool: ' + toolId);
      }

      return of(`${downloadToolsUrl}${downloadToolsDcaPath}`);
    }

    if (toolId === TestAutomationTools.dca_mac) {
      const downloadToolsDcaPath: string = this.configService.getDownloadToolsDcaPathMac();
      if (SharedCommonUtility.isNullish(downloadToolsDcaPath)) {
        throw new Error('Unknown tool path for tool: ' + toolId);
      }

      return of(`${downloadToolsUrl}${downloadToolsDcaPath}`);
    }

    if (this.isLevelDocsTool(toolId)) {
      const levelDocsPathPrefix = this.configService.getDownloadToolsLevelDocsPath();
      if (SharedCommonUtility.isNullish(levelDocsPathPrefix)) {
        throw new Error('Unknown tool path for tool: ' + toolId);
      }
      const url = `${downloadToolsUrl}${levelDocsPathPrefix}/${toolId}.msi`;
      return of(url);
    }

    return this.httpClient.get<Record<TestAutomationTools, string>>(`${downloadToolsUrl}/${downloadToolsIndexPath}`).pipe(
      map((response: Record<TestAutomationTools, string>): string => {
        const toolPath: string = response[toolId];

        if (SharedCommonUtility.isNullishOrEmpty(toolPath)) {
          throw new Error('Unknown tool path for tool: ' + toolId);
        }
        return `${downloadToolsUrl}/${toolPath}`;
      }),
    );
  }

  public getMobileSdkVersion(toolId: TestAutomationTools): Observable<string> {
    const fallbackVersion: string = toolId === TestAutomationTools.mobile_engine_android_sdk ? 'latest.integration' : 'latest';

    if (!this.configService.usesMobileEngineSdkToolsIndexVersion()) {
      return of(fallbackVersion);
    }

    const downloadToolsUrl: string = this.configService.getDownloadToolsUrl();
    const downloadToolsIndexPath: string = this.configService.getDownloadToolsIndexPath();

    return this.httpClient
      .get<Record<string, string>>(`${downloadToolsUrl}/${downloadToolsIndexPath}`)
      .pipe(map((index: Record<string, string>): string => index[toolId] ?? fallbackVersion));
  }

  public isLevelDocsTool(toolId: TestAutomationTools): boolean {
    return toolId.startsWith('leveldocs_');
  }
}
